import dayjs from 'dayjs'
import {Diagnosis, DiagnosisVersion} from '@/features/projects/types'

/**
 * Date ICD-10-IM becomes the mandatory classification. Until then ICD-9-CM and ICD-10-IM coexist
 * and codes are picked in ICD-9; from this date on they are picked in ICD-10.
 */
export const ICD10_MANDATORY_FROM = '2027-01-01'

/**
 * Classification in force. Both selects stay searchable during the coexistence, so this only decides
 * whether ICD-9 may still be picked: from ICD10_MANDATORY_FROM the ICD-9 select goes read-only and is
 * filled in from the ICD-10 instead.
 */
export const getDiagnosisSelectionVersion = (): DiagnosisVersion =>
    dayjs().isBefore(ICD10_MANDATORY_FROM) ? 'ICD9' : 'ICD10'

/**
 * ICD-10 proposed when an ICD-9 is picked. ICD-9 maps to up to 47 ICD-10 codes (23% of the mapped
 * ICD-9s are 1:N), so one has to be chosen: this mirrors the backend DiagnosisService.resolveToVersion
 * — sort by code, take the first.
 *
 * The transcoding is a suggestion, not an equivalence: the regional table has 1:N and N:1 relations
 * and does not always resolve. So callers must only use this to fill an ICD-10 that is still empty —
 * the two codes are stored as independent fields, and one the doctor picked is never overwritten.
 */
export const getFirstMappedCode = (diagnosis?: Diagnosis | null): Diagnosis | null =>
    [...(diagnosis?.mappedCodes ?? [])].sort((a, b) => a.code.localeCompare(b.code)).at(0) ?? null

/** Display names of the classifications. Proper nouns, identical in every locale. */
export const DIAGNOSIS_VERSION_LABELS: Record<DiagnosisVersion, string> = {
    ICD9: 'ICD-9',
    ICD10: 'ICD-10'
}
